import { inject, Injectable, isDevMode } from "@angular/core";

import { LgTranslateService } from "@logex/framework/lg-localization";
import { LgPromptDialog, LgSnackbarService } from "@logex/framework/ui-core";
import { ApplicationEventTracerHub } from "@logex/framework/core";

import { LgIdleTrackingService } from "./lg-idle-tracking.service";

/**
 * Error tracking entry with timestamp for deduplication
 */
interface TrackedError {
    guid: string;
    timestamp: number;
    url?: string | undefined;
    status?: number | undefined;
}

/**
 * Service for tracking network errors (5xx, network failures, OIDC errors).
 * Generates unique GUIDs, logs via ApplicationEventTracerHub, and shows user-facing modal.
 * Handles error storms with deduplication (5-second cooldown).
 *
 * Idle recovery: when the page was hidden for a prolonged period (e.g., laptop lid closed)
 * and network errors occur upon return, the service auto-reloads the page instead of
 * showing the error dialog. After reload, it either shows a capped error dialog (≤3 GUIDs)
 * or a persistent info snackbar if no further errors occur.
 *
 * This service is specifically for network/connectivity failures, not application bugs.
 * For general application error handling, see lg-error-handler.
 */
@Injectable({ providedIn: "root" })
export class LgNetworkErrorService {
    private _tracerHub = inject(ApplicationEventTracerHub);
    private _promptDialog = inject(LgPromptDialog);
    private _snackbar = inject(LgSnackbarService);
    private _lgTranslate = inject(LgTranslateService);
    private _idleTracker = inject(LgIdleTrackingService);

    private _trackedErrors: TrackedError[] = [];
    private _cooldownTimeoutId: ReturnType<typeof setTimeout> | null = null;
    private _isModalVisible = false;
    private _hasFatalError = false;

    private _isPostReloadMonitoring = false;
    private _postReloadGraceTimeoutId: ReturnType<typeof setTimeout> | null = null;

    private static readonly COOLDOWN_MS = 5000;
    private static readonly ERROR_RETENTION_MS = 10000;
    private static readonly IDLE_THRESHOLD_MS = 5 * 60 * 1000;
    private static readonly MAX_DISPLAYED_GUIDS = 3;
    private static readonly POST_RELOAD_GRACE_MS = 15_000;
    private static readonly SESSION_KEY_RELOAD_REASON = "lg_network_error_reload";

    constructor() {
        this._checkPostReloadState();
    }

    /**
     * Track a network error. Generates GUID, logs via tracer hub, handles deduplication.
     * This method never throws - errors during tracking are logged in dev mode but suppressed in production.
     *
     * If the page was idle for a prolonged period, triggers auto-reload instead of showing the dialog.
     *
     * @param error The error object or message
     * @param context Additional context (url, status code, etc.)
     * @param options Additional options
     * @param options.isFatal When true, the error modal will include a "Reload" button
     * @returns The generated GUID, or null if tracking fails
     */
    trackError(
        error: Error | string,
        context?: { url?: string; status?: number },
        options?: { isFatal?: boolean }
    ): string | null {
        try {
            const guid = this._generateGuid();
            const timestamp = Date.now();

            // Always log to console with GUID (even in production) for debugging
            const errorMessage = error instanceof Error ? error.message : String(error);
            console.error(`[Network Error] GUID: ${guid}`, {
                error: errorMessage,
                url: context?.url,
                status: context?.status,
                timestamp: new Date(timestamp).toISOString()
            });

            // Mark session as fatal if requested
            if (options?.isFatal) {
                this._hasFatalError = true;
            }

            // Log via tracer hub immediately (always log, even if modal suppressed)
            this._logToTracerHub(guid, error, context);

            // Add to tracked errors
            const trackedError: TrackedError = {
                guid,
                timestamp,
                url: context?.url,
                status: context?.status
            };
            this._trackedErrors.push(trackedError);

            // Clean up old errors
            this._cleanupOldErrors();

            // If we're in post-reload monitoring and new errors arrive, cancel grace period
            // and let the normal modal flow handle it (with capped GUIDs)
            if (this._isPostReloadMonitoring) {
                this._cancelPostReloadGracePeriod();
            }

            // Check if errors occurred after prolonged idle — auto-reload if not already
            // a post-reload attempt (one-shot guard)
            if (this._shouldAutoReload()) {
                this._performIdleReload();
                return guid;
            }

            // Handle deduplication and modal display
            this._scheduleModalDisplay();

            return guid;
        } catch (trackingError) {
            // Never throw from trackError - this could break the application
            if (isDevMode()) {
                console.error("Network error tracking failed:", trackingError);
            }
            return null;
        }
    }

    private _getRecentGuids(): string[] {
        this._cleanupOldErrors();
        return this._trackedErrors.map(e => e.guid);
    }

    private _shouldAutoReload(): boolean {
        // Don't auto-reload if this is already a post-reload attempt
        if (sessionStorage.getItem(LgNetworkErrorService.SESSION_KEY_RELOAD_REASON)) {
            return false;
        }

        return this._idleTracker.wasRecentlyIdle(LgNetworkErrorService.IDLE_THRESHOLD_MS);
    }

    private _performIdleReload(): void {
        sessionStorage.setItem(
            LgNetworkErrorService.SESSION_KEY_RELOAD_REASON,
            JSON.stringify({ reason: "idle-network-error", timestamp: Date.now() })
        );
        window.location.reload();
    }

    private _checkPostReloadState(): void {
        const reloadData = sessionStorage.getItem(LgNetworkErrorService.SESSION_KEY_RELOAD_REASON);
        if (!reloadData) {
            return;
        }

        // Clear the flag immediately
        sessionStorage.removeItem(LgNetworkErrorService.SESSION_KEY_RELOAD_REASON);

        // Enter post-reload monitoring mode
        this._isPostReloadMonitoring = true;

        // Start grace period: if no new errors arrive, show info snackbar
        this._postReloadGraceTimeoutId = setTimeout(() => {
            this._postReloadGraceTimeoutId = null;
            this._isPostReloadMonitoring = false;
            this._showPostReloadInfoSnackbar();
        }, LgNetworkErrorService.POST_RELOAD_GRACE_MS);
    }

    private _cancelPostReloadGracePeriod(): void {
        if (this._postReloadGraceTimeoutId !== null) {
            clearTimeout(this._postReloadGraceTimeoutId);
            this._postReloadGraceTimeoutId = null;
        }
        this._isPostReloadMonitoring = false;
    }

    private _showPostReloadInfoSnackbar(): void {
        this._snackbar.show({
            title: this._lgTranslate.translate("FW.ERROR_TRACKING.RELOAD_INFO_TITLE"),
            text: this._lgTranslate.translate("FW.ERROR_TRACKING.RELOAD_INFO_MESSAGE"),
            autoHide: false
        });
    }

    private _generateGuid(): string {
        // Use crypto.randomUUID() for UUID v4 generation
        if (typeof crypto !== "undefined" && crypto.randomUUID) {
            return crypto.randomUUID();
        }
        throw new Error("crypto.randomUUID is not available");
    }

    private _logToTracerHub(
        guid: string,
        error: Error | string,
        context?: { url?: string; status?: number }
    ): void {
        const errorObj = error instanceof Error ? error : new Error(String(error));
        const customProperties = {
            errorTrackingId: guid,
            errorType: "network",
            url: context?.url,
            statusCode: context?.status
        };

        this._tracerHub.trackException(errorObj, customProperties);
    }

    private _cleanupOldErrors(): void {
        const cutoff = Date.now() - LgNetworkErrorService.ERROR_RETENTION_MS;
        this._trackedErrors = this._trackedErrors.filter(e => e.timestamp > cutoff);
    }

    private _scheduleModalDisplay(): void {
        // If modal is already visible, don't schedule another
        if (this._isModalVisible) {
            return;
        }

        // If already in cooldown, the existing timeout will handle it
        if (this._cooldownTimeoutId !== null) {
            return;
        }

        // Start cooldown period
        this._cooldownTimeoutId = setTimeout(() => {
            this._showErrorModal();
            this._cooldownTimeoutId = null;
        }, LgNetworkErrorService.COOLDOWN_MS);
    }

    private _showErrorModal(): void {
        const allGuids = this._getRecentGuids();
        if (allGuids.length === 0) {
            return;
        }

        this._isModalVisible = true;

        const displayedGuids = allGuids.slice(0, LgNetworkErrorService.MAX_DISPLAYED_GUIDS);
        const hiddenCount = allGuids.length - displayedGuids.length;

        const title = this._lgTranslate.translate("FW.ERROR_TRACKING.TITLE");
        let message: string;

        if (displayedGuids.length === 1 && hiddenCount === 0) {
            message = this._lgTranslate.translate("FW.ERROR_TRACKING.MESSAGE_SINGLE", {
                guid: displayedGuids[0]
            });
        } else {
            const guidList = displayedGuids.map(g => `<code>${g}</code>`).join("<br>");
            message = this._lgTranslate.translate("FW.ERROR_TRACKING.MESSAGE_MULTIPLE", {
                guids: guidList,
                count: allGuids.length
            });

            if (hiddenCount > 0) {
                message += `<br><br>${this._lgTranslate.translate("FW.ERROR_TRACKING.MESSAGE_MORE", { count: hiddenCount })}`;
            }
        }

        const isFatal = this._hasFatalError;

        const dialogPromise = isFatal
            ? this._promptDialog.alert(title, message, {
                  allowClose: true,
                  columns: 4,
                  buttons: [
                      {
                          id: "reload",
                          name: "Reload",
                          nameLc: "FW.ERROR_TRACKING.RELOAD",
                          class: "button--primary",
                          isConfirmAction: true
                      }
                  ]
              })
            : this._promptDialog.alert(title, message, {
                  allowClose: true,
                  columns: 4
              });

        dialogPromise
            .then(response => {
                if (response === "reload") {
                    window.location.reload();
                }
            })
            .finally(() => {
                this._isModalVisible = false;
                // Clear tracked errors after showing modal
                this._trackedErrors = [];
            });
    }
}
